"use client";

import React, { useState } from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiPlus, PiTrash, PiQuotesFill, PiArrowUpRight, PiDotsSixVertical } from "react-icons/pi";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// --- Sortable Item Component ---
function SortableTestimonialCard({
    item,
    idx,
    isSelected,
    updateTestimonial,
    removeTestimonial
}: {
    item: any;
    idx: number;
    isSelected: boolean;
    updateTestimonial: (idx: number, field: string, value: string) => void;
    removeTestimonial: (idx: number) => void;
}) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: item.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 50 : "auto",
        position: 'relative' as 'relative', // Typescript fix
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className="group relative bg-[#15151f] rounded-2xl p-8 border border-white/5 shadow-2xl hover:shadow-pink-500/10 hover:border-pink-500/30 transition-all duration-500 hover:-translate-y-2 h-full flex flex-col"
        >
            {/* Drag Handle & Delete Button */}
            {isSelected && (
                <div className="absolute -top-3 -right-3 flex gap-2 z-20 opacity-0 group-hover:opacity-100 transition-opacity">
                    {/* Drag Handle */}
                    <button
                        {...attributes}
                        {...listeners}
                        className="h-8 w-8 flex items-center justify-center rounded-full bg-gray-700 text-white shadow-lg cursor-grab active:cursor-grabbing hover:bg-gray-600"
                        title="Drag to reorder"
                    >
                        <PiDotsSixVertical className="h-4 w-4" />
                    </button>
                    {/* Delete Button */}
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            removeTestimonial(idx);
                        }}
                        className="h-8 w-8 flex items-center justify-center rounded-full bg-red-500/90 text-white shadow-lg hover:bg-red-600"
                        title="Remove"
                    >
                        <PiTrash className="h-4 w-4" />
                    </button>
                </div>
            )}

            {/* Quote Icon */}
            <div className="mb-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500/20 to-rose-500/20 flex items-center justify-center border border-pink-500/30">
                    <PiQuotesFill className="h-6 w-6 text-pink-400" />
                </div>
            </div>

            {/* Testimonial Text */}
            <p className="text-gray-300 leading-relaxed mb-8 text-base font-light flex-grow">
                <EditableText
                    value={item.text}
                    onChange={(val) => updateTestimonial(idx, "text", val)}
                    as="span"
                    placeholder="Testimonial text goes here..."
                />
            </p>

            {/* Author Section */}
            <div className="flex items-center gap-4 pt-6 border-t border-white/5 mt-auto">
                <div className="h-12 w-12 rounded-full bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center text-white font-semibold overflow-hidden shadow-lg shadow-pink-500/20 shrink-0">
                    {item.image || item.avatar ? (
                        <img
                            src={item.image || item.avatar}
                            alt={item.name}
                            className="h-full w-full object-cover"
                        />
                    ) : (
                        <span className="text-sm">
                            {item.name?.charAt(0) || "U"}
                        </span>
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-white font-semibold text-sm truncate">
                        <EditableText
                            value={item.name}
                            onChange={(val) => updateTestimonial(idx, "name", val)}
                            as="span"
                            placeholder="Client Name"
                        />
                    </div>
                    <div className="text-gray-500 text-xs truncate">
                        <EditableText
                            value={item.role}
                            onChange={(val) => updateTestimonial(idx, "role", val)}
                            as="span"
                            placeholder="Role / Company"
                        />
                    </div>
                </div>
                <a
                    href="#"
                    className="text-pink-500 hover:text-pink-400 transition-colors opacity-0 group-hover:opacity-100 transform translate-x-[-10px] group-hover:translate-x-0 duration-300"
                >
                    <PiArrowUpRight className="h-5 w-5" />
                </a>
            </div>

            {/* Hover gradient overlay */}
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-pink-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
        </div>
    );
}

export default function TestimonialsSaaS({ content, onContentChange, isSelected = false }: SectionContentProps) {
    const fontFamily = content.fontFamily || "'Inter', sans-serif";

    // Ensure items have IDs for drag and drop
    const testimonials = (content.testimonials || [
        {
            id: "1",
            name: "Sarah Johnson",
            role: "Product Manager",
            text: "This platform has completely transformed how we handle our workflow.",
        },
        {
            id: "2",
            name: "Michael Chen",
            role: "Tech Lead",
            text: "The developer experience is exceptional. Clean APIs and documentation.",
        },
        {
            id: "3",
            name: "Emily Rodriguez",
            role: "Startup Founder",
            text: "Scaling our operations was seamless with this solution.",
        },
    ]).map((t: any, i: number) => ({ ...t, id: t.id || `test-${i}-${Date.now()}` }));

    const addTestimonial = () => {
        const newItems = [
            ...testimonials,
            {
                id: `new-${Date.now()}`,
                name: "New Client",
                role: "Role Here",
                text: "Enter testimonial text here.",
            },
        ];
        onContentChange?.({ ...content, testimonials: newItems });
    };

    const removeTestimonial = (idx: number) => {
        const newItems = testimonials.filter((_: any, i: number) => i !== idx);
        onContentChange?.({ ...content, testimonials: newItems });
    };

    const updateTestimonial = (idx: number, field: string, value: string) => {
        const newItems = [...testimonials];
        newItems[idx] = { ...newItems[idx], [field]: value };
        onContentChange?.({ ...content, testimonials: newItems });
    };

    // DND Sensors
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = testimonials.findIndex((item: any) => item.id === active.id);
            const newIndex = testimonials.findIndex((item: any) => item.id === over.id);

            const newItems = arrayMove(testimonials, oldIndex, newIndex);
            onContentChange?.({ ...content, testimonials: newItems });
        }
    };

    return (
        <div
            className={`relative overflow-hidden ${isSelected ? "ring-2 ring-blue-500" : ""}`}
            style={{ fontFamily }}
        >
            {/* Backgrounds */}
            <div className="absolute inset-0 bg-[#0a0a0f]" />
            <div className="absolute inset-0 bg-gradient-to-br from-[#0a0a0f] via-[#12121a] to-[#1a1a25]" />
            <div className="absolute top-20 left-10 w-72 h-72 bg-pink-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />

            <div className="relative z-10 px-4 py-24 sm:px-6 lg:px-8">
                <div className="mx-auto max-w-7xl">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl sm:text-5xl font-bold text-white tracking-tight mb-4">
                            <EditableText
                                value={content.title || "What They Say"}
                                onChange={(val) => onContentChange?.({ ...content, title: val })}
                                as="span"
                                placeholder="What They Say"
                            />
                        </h2>
                        <div className="w-24 h-1 bg-gradient-to-r from-pink-500 to-rose-500 mx-auto rounded-full" />
                    </div>

                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext
                            items={testimonials.map((t: any) => t.id)}
                            strategy={rectSortingStrategy}
                        >
                            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                                {testimonials.map((item: any, idx: number) => (
                                    <div key={item.id} className="h-full">
                                        <SortableTestimonialCard
                                            item={item}
                                            idx={idx}
                                            isSelected={isSelected}
                                            updateTestimonial={updateTestimonial}
                                            removeTestimonial={removeTestimonial}
                                        />
                                    </div>
                                ))}

                                {isSelected && (
                                    <button
                                        onClick={addTestimonial}
                                        className="group relative flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-white/10 bg-[#15151f]/50 p-8 text-gray-500 hover:border-pink-500/50 hover:bg-[#15151f] hover:text-pink-400 transition-all min-h-[320px]"
                                    >
                                        <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mb-4 group-hover:bg-pink-500/20 transition-colors">
                                            <PiPlus className="h-8 w-8" />
                                        </div>
                                        <span className="text-lg font-medium">Add Testimonial</span>
                                    </button>
                                )}
                            </div>
                        </SortableContext>
                    </DndContext>
                </div>
            </div>
        </div>
    );
}
